import React from "react";

import { Switch } from "antd";

import Icon from "shared/components/Icon";
import Table from "shared/components/Table";
import { RegularText } from "shared/components/Typography";

import CosignCopyUuid from "./CosignCopyUuid";
import CosignStatusBadge from "./CosignStatusBadge";
import {
  COSIGN_DASHBOARD_ACTION_LABELS,
  CosignMappedTableProps,
  formatCosignSectionTitle,
} from "./cosignDashboard.config";
import {
  CosignDisabledLabel,
  InactiveMappedRowCell,
  TableHeader,
  TableSection,
  TableWrapper,
} from "./styled";

const wrapInactiveCell = (isActive: boolean, content: React.ReactNode) => (
  <InactiveMappedRowCell $inactive={!isActive}>{content}</InactiveMappedRowCell>
);

/**
 * Section 1 table — VERO properties with a Cosign mapping (PRODUCT-988).
 */
const CosignMappedPropertiesTable: React.FC<CosignMappedTableProps> = ({
  properties,
  isLoading,
  onDisconnect,
  onToggle,
  isToggleUpdating = false,
}) => (
  <TableSection data-testid="cosign-mapped-section">
    <TableHeader>
      <h2>
        {formatCosignSectionTitle(
          "Cosign Enabled Properties",
          properties.length
        )}
      </h2>
    </TableHeader>
    <TableWrapper>
      <Table
        alt
        noClickableRows
        loading={isLoading}
        dropdownColumnTitle="Actions"
        columns={[
          { title: "VERO Property", dataIndex: "name", key: "name" },
          { title: "Address", dataIndex: "address", key: "address" },
          {
            title: "Cosign Property",
            dataIndex: "cosignPropertyName",
            key: "cosignPropertyName",
          },
          {
            title: "Cosign Property UUID",
            dataIndex: "cosignPropertyUuid",
            key: "cosignPropertyUuid",
          },
          {
            title: "Property Manager UUID",
            dataIndex: "propertyManagerUuid",
            key: "propertyManagerUuid",
          },
          { title: "Cosign", dataIndex: "isActive", key: "isActive" },
          { title: "Status", dataIndex: "cosignStatus", key: "cosignStatus" },
        ]}
        rows={properties.map((property) => ({
          key: String(property.mappingId),
          values: [
            wrapInactiveCell(
              property.isActive,
              <RegularText key={`${property.mappingId}-name`} strong>
                {property.name}
                {!property.isActive && (
                  <CosignDisabledLabel
                    data-testid={`cosign-disabled-label-${property.mappingId}`}
                  >
                    Disabled
                  </CosignDisabledLabel>
                )}
              </RegularText>
            ),
            wrapInactiveCell(
              property.isActive,
              <RegularText key={`${property.mappingId}-address`}>
                {property.address}
              </RegularText>
            ),
            wrapInactiveCell(
              property.isActive,
              <RegularText key={`${property.mappingId}-cosignPropertyName`}>
                {property.cosignPropertyName || "—"}
              </RegularText>
            ),
            wrapInactiveCell(
              property.isActive,
              <CosignCopyUuid
                key={`${property.mappingId}-cosignPropertyUuid`}
                uuid={property.cosignPropertyUuid}
                label="Cosign property UUID"
                testId={`cosign-property-uuid-${property.mappingId}`}
              />
            ),
            wrapInactiveCell(
              property.isActive,
              <CosignCopyUuid
                key={`${property.mappingId}-propertyManagerUuid`}
                uuid={property.propertyManagerUuid}
                label="Property manager UUID"
                testId={`cosign-pm-uuid-${property.mappingId}`}
              />
            ),
            <Switch
              key={`${property.mappingId}-isActive`}
              checked={property.isActive}
              disabled={isToggleUpdating}
              size="small"
              onChange={(checked) => onToggle(property, checked)}
            />,
            wrapInactiveCell(
              property.isActive,
              <CosignStatusBadge
                key={`${property.mappingId}-cosignStatus`}
                status={property.cosignStatus}
              />
            ),
          ],
          dropdownItems: [
            {
              key: `cosign-disconnect-${property.mappingId}`,
              label: COSIGN_DASHBOARD_ACTION_LABELS.disconnect,
              onClick: () => onDisconnect(property),
            },
          ],
        }))}
        columnBreakpoints={{
          xs: {
            column1: "name",
            column2: "isActive",
            column3: "cosignStatus",
          },
          sm: ["name", "address", "isActive", "cosignStatus"],
          md: ["name", "address", "cosignPropertyName", "isActive", "cosignStatus"],
          lg: [
            "name",
            "address",
            "cosignPropertyName",
            "cosignPropertyUuid",
            "propertyManagerUuid",
            "isActive",
            "cosignStatus",
          ],
        }}
        placeholder={{
          Icon: Icon.NoDataIcon,
          label: "No properties are mapped to Cosign yet",
        }}
      />
    </TableWrapper>
  </TableSection>
);

export default CosignMappedPropertiesTable;
